import {
  ConfirmExacts,
  ConfirmExactsResult,
  DisclosureChecklist,
  DisclosureChecklistId,
  DisclosureChecklistLine,
  DisclosureChecklistLineId,
  DisclosureChecklistMin,
  DisclosureChecklistSummary,
  DisclosureChecklistTemplateId,
  DisclosureChecklistTemplateMin,
  EngagementId,
  LineHistoryEntry,
  UpdateLine,
} from "platformed-client/types";
import {invalidateQueries, q, queryClient, setQueryData} from "../../state";
import {platformedClient} from "..";

export async function list(engagementId: EngagementId): Promise<DisclosureChecklistSummary[]> {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/engagements/{engagement_id}/disclosures/checklists",
    {path: {engagement_id: engagementId}},
  );
}

export async function get(checklistId: DisclosureChecklistId): Promise<DisclosureChecklist> {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/disclosure_checklists/{checklist_id}",
    {path: {checklist_id: checklistId}},
  );
}

export async function lineHistory(
  checklistId: DisclosureChecklistId,
  lineId: DisclosureChecklistLineId,
): Promise<LineHistoryEntry[]> {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/disclosure_checklists/{checklist_id}/lines/{line_id}/history",
    {path: {checklist_id: checklistId, line_id: lineId}},
  );
}

const invalidateCounts = (engagementId: EngagementId) =>
  invalidateQueries([q.tprm.disclosureChecklists.filter(engagementId), q.tprm.disclosureSummary.filter(engagementId)]);

export async function create(
  engagementId: EngagementId,
  templateId: DisclosureChecklistTemplateId,
): Promise<DisclosureChecklistMin> {
  const created = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/disclosures/checklists",
    {path: {engagement_id: engagementId}, body: {template_id: templateId}},
  );
  await invalidateCounts(engagementId);
  return created;
}

/** A write moves the counts of its checklist's engagement. */
export type ChecklistRef = Pick<DisclosureChecklist, "checklist_id" | "engagement_id">;

/**
 * The returned line is patched into the cached checklist unless a newer revision is already there.
 * The checklist refetches either way, for the lines' effect on `changed_since_approval`.
 */
export async function updateLine(
  checklist: ChecklistRef,
  lineId: DisclosureChecklistLineId,
  body: UpdateLine,
): Promise<DisclosureChecklistLine> {
  try {
    const line = await platformedClient.internalRequest(
      "patch",
      "/region/{region}/api/tprm/disclosure_checklists/{checklist_id}/lines/{line_id}",
      {path: {checklist_id: checklist.checklist_id, line_id: lineId}, body},
    );
    await setQueryData(q.tprm.disclosureChecklist(checklist.checklist_id), (old: DisclosureChecklist | undefined) =>
      old == null
        ? old
        : {
            ...old,
            lines: old.lines.map(l =>
              l.line_id === line.line_id && (l.revision ?? 0) <= (line.revision ?? 0) ? line : l,
            ),
          },
    );
    return line;
  } finally {
    await invalidateQueries([
      q.tprm.disclosureChecklist.filter(checklist.checklist_id),
      q.tprm.disclosureChecklistLineHistory.filter(checklist.checklist_id, lineId),
    ]);
    await invalidateCounts(checklist.engagement_id);
  }
}

export async function confirmExacts(checklist: ChecklistRef, body: ConfirmExacts): Promise<ConfirmExactsResult> {
  try {
    return await platformedClient.internalRequest(
      "post",
      "/region/{region}/api/tprm/disclosure_checklists/{checklist_id}/confirm_exacts",
      {path: {checklist_id: checklist.checklist_id}, body},
    );
  } finally {
    await invalidateQueries([
      q.tprm.disclosureChecklist.filter(checklist.checklist_id),
      q.tprm.disclosureChecklistLineHistory.filter(checklist.checklist_id),
    ]);
    await invalidateCounts(checklist.engagement_id);
  }
}

/** The new version's content loads before the checklist refetches, so the page doesn't fall back to its spinner. */
export async function upgrade(
  checklist: ChecklistRef,
  target: DisclosureChecklistTemplateMin,
): Promise<DisclosureChecklistMin> {
  try {
    const upgraded = await platformedClient.internalRequest(
      "post",
      "/region/{region}/api/tprm/disclosure_checklists/{checklist_id}/upgrade",
      {path: {checklist_id: checklist.checklist_id}, body: {template_id: target.disclosure_checklist_template_id}},
    );
    await queryClient.prefetchQuery(
      q.tprm.disclosureChecklistTemplate(
        target.model.disclosure_checklist_model_id,
        target.disclosure_checklist_template_id,
      ),
    );
    return upgraded;
  } finally {
    await invalidateQueries([
      q.tprm.disclosureChecklistModels.filter(),
      q.tprm.disclosureChecklistModel.filter(target.model.disclosure_checklist_model_id),
      q.tprm.disclosureChecklist.filter(checklist.checklist_id),
      q.tprm.disclosureChecklistLineHistory.filter(checklist.checklist_id),
    ]);
    await invalidateCounts(checklist.engagement_id);
  }
}
